import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator, TouchableOpacity, TextInput, ScrollView, Modal } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { api, apiDelete, getToken } from '../../src/api/client';
import { config } from '../../src/config';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import QrSvgView from '../../src/components/QrSvgView';
import { downloadFile, exportPdfFromUrl } from '../../src/utils/fileExport';
import InlineConfirm from '../../src/components/InlineConfirm';
import { AdminToolbar } from '../../src/components/AdminList';
import { useTheme } from '../../src/context/ThemeContext';
import { useResponsive, rs, fs } from '../../src/utils/responsive';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { useModalBottomPadding } from '../../src/utils/useModalBottomPadding';

interface LocationItem {
  id: string;
  name: string;
  companyName: string | null;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string | null;
  description: string | null;
  isActive: boolean;
  ownerEmail: string | null;
  businessName: string | null;
  ownerId: string;
  stripeOnboarded: boolean;
  bayCount: number;
  createdAt: string;
}

interface BayItem {
  id: string;
  name: string;
  type: string;
  pricePerMinute: number | null;
  minimumCharge: number | null;
  maximumCharge: number | null;
  tiers: any[] | null;
  presets: number[] | null;
  allowCustomAmount: boolean;
  isActive: boolean;
  controllerId: string | null;
  qrCodeUrl: string | null;
  deepLink: string;
}

/** AdminLocations - CRUD for locations and bays with inline editing modals */
export default function AdminLocations() {
  const { colors } = useTheme();
  const { width, height } = useResponsive();
  const isMobile = width < 768;
  const styles = useStyles(colors, isMobile, height);
  const modalBottomPadding = useModalBottomPadding();
  const { ownerId: paramOwnerId } = useLocalSearchParams<{ ownerId?: string }>();
  const router = useRouter();
  const [locations, setLocations] = useState<LocationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  /* Expanded location — tracks which location's bays are shown */
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [locationBays, setLocationBays] = useState<BayItem[]>([]);
  const [locationMachines, setLocationMachines] = useState<{ id: string; name: string; machineType?: string | null; controllerId?: string | null; isActive?: boolean; equipment?: { controllers: number; online: number; configured: boolean } }[]>([]); // Nathan 10-18: admin twin for vending equipment
  const [baysLoading, setBaysLoading] = useState(false);

  /* Edit modal state for location or bay */
  const [editModal, setEditModal] = useState<'location' | 'bay' | null>(null);
  const [editData, setEditData] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  /* Delete confirmation state */
  const [confirmDeleteLocation, setConfirmDeleteLocation] = useState<string | null>(null);
  const [confirmDeleteBay, setConfirmDeleteBay] = useState<string | null>(null);
  const [deletingLocation, setDeletingLocation] = useState(false);
  const [deletingBay, setDeletingBay] = useState(false);

  /* Bay QR modal + aggregate QR PDF modal state */
  const [showQrBay, setShowQrBay] = useState<BayItem | null>(null);
  const [qrPdfLocation, setQrPdfLocation] = useState<LocationItem | null>(null);
  const [qrPdfSize, setQrPdfSize] = useState('480');
  const [qrPdfDownloading, setQrPdfDownloading] = useState(false);

  /* Create bay modal state */
  const [createBayModal, setCreateBayModal] = useState(false);
  const [createBayLocationId, setCreateBayLocationId] = useState<string | null>(null);
  const [createBayData, setCreateBayData] = useState<any>(null);
  const [creating, setCreating] = useState(false);

  const fetchLocations = useCallback(async (p = 1, s = '', signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '20' });
      if (s) params.set('search', s);
      if (paramOwnerId) params.set('ownerId', paramOwnerId);
      const data = await api<{ locations: LocationItem[]; total: number }>(`/api/admin/locations?${params}`);
      if (signal?.aborted) return;
      setLocations(data.locations);
      setTotal(data.total);
      setPage(p);
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, [paramOwnerId]);

  useEffect(() => { const ac = new AbortController(); fetchLocations(1, '', ac.signal); return () => ac.abort(); }, [paramOwnerId]);

  const handleSearch = () => fetchLocations(1, search);
  const handlePage = (p: number) => fetchLocations(p, search);

  const toggleExpand = async (locId: string) => {
    if (expandedId === locId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(locId);
    setBaysLoading(true);
    try {
      const data = await api<{ bays: BayItem[] }>(`/api/admin/locations/${locId}/bays`);
      setLocationBays(data.bays);
    } catch { setLocationBays([]); }
    finally { setBaysLoading(false); }
    // Vending machines: the owner list route serves any staff role with the full shape (silent: section stays empty on failure).
    try { setLocationMachines(await api<any[]>(`/api/locations/${locId}/vending-machines`)); } catch { setLocationMachines([]); }
  };

  const openEditLocation = async (loc: LocationItem) => {
    // Fetch full location details including hours
    try {
      const full = await api<any>(`/api/admin/locations/${loc.id}`);
      setEditData({
        id: loc.id, name: full.name, companyName: full.companyName || '', address: full.address,
        city: full.city, state: full.state, zip: full.zip, phone: full.phone || '',
        description: full.description || '', isActive: full.isActive,
        hours: full.hours || { mon: '', tue: '', wed: '', thu: '', fri: '', sat: '', sun: '' },
      });
    } catch { /* full location fetch failed — use list data as fallback */
      setEditData({
        id: loc.id, name: loc.name, companyName: loc.companyName || '', address: loc.address,
        city: loc.city, state: loc.state, zip: loc.zip, phone: loc.phone || '',
        description: loc.description || '', isActive: loc.isActive,
        hours: { mon: '', tue: '', wed: '', thu: '', fri: '', sat: '', sun: '' },
      });
    }
    setEditModal('location');
  };

  const openEditBay = (bay: BayItem) => {
    setEditData({
      id: bay.id, name: bay.name, type: bay.type, isActive: bay.isActive,
      pricePerMinute: bay.pricePerMinute?.toString() || '',
      minimumCharge: bay.minimumCharge?.toString() || '',
      maximumCharge: bay.maximumCharge?.toString() || '',
      controllerId: bay.controllerId || '',
      tiers: bay.tiers ? JSON.stringify(bay.tiers) : '',
      // Structured timed-package rows (self-serve + vacuum both carry the
      // packages family now): Name | Price ($) | Minutes. Empty = family off.
      vacTiers: bay.type !== 'automatic' && bay.tiers
        ? bay.tiers.map((t: any) => ({
            name: t.name ?? '',
            price: ((t.price ?? 0) / 100).toFixed(2),
            duration: String(Math.round((t.durationMs || 0) / 60000)),
          }))
        : [],
      presets: bay.presets ? bay.presets.join(', ') : '',
      allowCustomAmount: bay.allowCustomAmount,
    });
    setEditModal('bay');
  };

  /** Build timed-package body.tiers from structured rows; toasts nothing
   *  (admin UI uses console) — returns null when a row is invalid, [] when
   *  no usable rows (packages family off). */
  const buildVacTiers = (rows: { name: string; price: string; duration: string }[]) => {
    const parsed = rows
      .filter(r => r.name.trim() || r.price || r.duration)
      .map(r => ({
        name: r.name.trim(),
        price: Math.round(parseFloat(r.price) * 100),
        durationMs: Math.round(parseFloat(r.duration) * 60000),
        enabled: true,
      }));
    if (parsed.some(t => !t.name || isNaN(t.price) || t.price <= 0
      || isNaN(t.durationMs) || t.durationMs < 60000 || t.durationMs > 3600000)) return null;
    return parsed;
  };

  const saveLocation = async () => {
    if (!editData) return;
    setSaving(true);
    try {
      const { id, hours, ...rest } = editData;
      await api(`/api/admin/locations/${id}`, { method: 'PUT', body: { ...rest, hours } });
      setEditModal(null);
      fetchLocations(page, search);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const saveBay = async () => {
    if (!editData) return;
    setSaving(true);
    try {
      const body: any = { name: editData.name, type: editData.type, isActive: editData.isActive, allowCustomAmount: editData.allowCustomAmount };
      body.controllerId = editData.controllerId || null;
      if (editData.type === 'self-serve' || editData.type === 'vacuum') {
        // One family or the other (Nathan 2026-08-09): package rows present →
        // Timed Packages (count down), metered max charge cleared; otherwise
        // Metered (count up), packages cleared.
        if (editData.pricePerMinute) body.pricePerMinute = parseFloat(editData.pricePerMinute);
        if (editData.minimumCharge) body.minimumCharge = parseInt(editData.minimumCharge);
        const pkgs = buildVacTiers(editData.vacTiers || []);
        if (!pkgs) {
          console.error('Invalid timed packages — each needs a name, price > 0, and 1-60 minutes');
          setSaving(false);
          return;
        }
        if (pkgs.length > 0) {
          body.tiers = pkgs;
          body.maximumCharge = null;
        } else {
          body.tiers = null;
          body.maximumCharge = editData.maximumCharge ? parseInt(editData.maximumCharge) : null;
        }
        if (editData.presets) {
          body.presets = editData.presets.split(',').map((s: string) => parseInt(s.trim())).filter((n: number) => !isNaN(n));
        } else { body.presets = null; }
      } else {
        if (editData.tiers) {
          try { body.tiers = JSON.parse(editData.tiers); } catch { /* non-critical: invalid JSON, skip tiers update */ }
        }
      }
      await api(`/api/admin/bays/${editData.id}`, { method: 'PUT', body });
      setEditModal(null);
      if (expandedId) toggleExpand(expandedId);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const openCreateBay = (locId: string, recreate = false) => {
    setCreateBayLocationId(locId);
    setCreateBayData({
      oldUrl: '', name: '', type: 'self-serve',
      pricePerMinute: '', minimumCharge: '', maximumCharge: '',
      controllerId: '', tiers: '', presets: '', allowCustomAmount: false, isActive: true,
      vacTiers: [],
      recreateMode: recreate,
    });
    setCreateBayModal(true);
  };

  /** Extract bay UUID from a wash URL like https://rinsepay.app/wash/{locationId}/{bayId} or accept raw UUID */
  const parseBayId = (input: string): string => {
    const trimmed = input.trim();
    if (!trimmed) return '';
    // Try to extract from URL pattern: /wash/{locationId}/{bayId}
    const urlMatch = trimmed.match(/\/wash\/[^/]+\/([0-9a-f-]{36})/i);
    if (urlMatch) return urlMatch[1];
    // Accept raw UUID
    if (/^[0-9a-f-]{36}$/i.test(trimmed)) return trimmed;
    return '';
  };

  const createBay = async () => {
    if (!createBayData || !createBayLocationId) return;
    setCreating(true);
    try {
      const body: any = { name: createBayData.name, type: createBayData.type, isActive: createBayData.isActive, allowCustomAmount: createBayData.allowCustomAmount };
      const bayId = parseBayId(createBayData.oldUrl);
      if (bayId) body.id = bayId;
      body.controllerId = createBayData.controllerId || null;
      if (createBayData.type === 'self-serve' || createBayData.type === 'vacuum') {
        // One family or the other: package rows present → Timed Packages
        // (count down, no metered max); otherwise Metered (count up).
        if (createBayData.pricePerMinute) body.pricePerMinute = parseFloat(createBayData.pricePerMinute);
        if (createBayData.minimumCharge) body.minimumCharge = parseInt(createBayData.minimumCharge);
        const pkgs = buildVacTiers(createBayData.vacTiers || []);
        if (!pkgs) {
          console.error('Invalid timed packages — each needs a name, price > 0, and 1-60 minutes');
          setCreating(false);
          return;
        }
        if (pkgs.length > 0) {
          body.tiers = pkgs;
        } else if (createBayData.maximumCharge) {
          body.maximumCharge = parseInt(createBayData.maximumCharge);
        }
        if (createBayData.presets) {
          body.presets = createBayData.presets.split(',').map((s: string) => parseInt(s.trim())).filter((n: number) => !isNaN(n));
        }
      } else {
        if (createBayData.tiers) {
          try { body.tiers = JSON.parse(createBayData.tiers); } catch { /* invalid JSON, skip */ }
        }
      }
      await api(`/api/admin/locations/${createBayLocationId}/bays`, { method: 'POST', body });
      setCreateBayModal(false);
      if (expandedId) toggleExpand(expandedId);
    } catch (err) { console.error(err); }
    finally { setCreating(false); }
  };

  /** Download the aggregate bay-label PDF for a location at the chosen label width. */
  const downloadQrPdf = async () => {
    if (!qrPdfLocation) return;
    const size = parseInt(qrPdfSize, 10) || 480;
    setQrPdfDownloading(true);
    try {
      const token = getToken();
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
      const url = `${config.apiUrl}/api/admin/locations/${qrPdfLocation.id}/bays/qr-pdf?size=${size}`;
      const safeName = qrPdfLocation.name.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-');
      await exportPdfFromUrl(url, headers, `rinsepay-bay-qr-${safeName}.pdf`);
      setQrPdfLocation(null);
    } catch (err) { console.error('QR PDF download failed:', err); }
    finally { setQrPdfDownloading(false); }
  };

  const handleDeleteLocation = async (id: string) => {
    setDeletingLocation(true);
    try {
      await apiDelete(`/api/admin/locations/${id}`);
      setConfirmDeleteLocation(null);
      fetchLocations(page, search);
    } catch (err: any) {
      console.error(err);
      setConfirmDeleteLocation(null);
    } finally { setDeletingLocation(false); }
  };

  const handleDeleteBay = async (id: string) => {
    setDeletingBay(true);
    try {
      await apiDelete(`/api/admin/bays/${id}`);
      setConfirmDeleteBay(null);
      if (expandedId) toggleExpand(expandedId);
    } catch (err: any) {
      console.error(err);
      setConfirmDeleteBay(null);
    } finally { setDeletingBay(false); }
  };

  const csvEndpoint = `/api/admin/locations?${new URLSearchParams({ ...(search && { search }) })}`;

  const renderItem = ({ item }: { item: LocationItem }) => (
    <View>
      <TouchableOpacity style={styles.row} onPress={() => toggleExpand(item.id)}>
        <View style={[styles.statusIcon, { backgroundColor: item.isActive ? '#05966915' : '#dc262615' }]}>
          <Ionicons name={item.isActive ? 'checkmark-circle' : 'close-circle'} size={18} color={item.isActive ? '#059669' : '#dc2626'} />
        </View>
        <View style={styles.rowBody}>
          <View style={styles.rowHeader}>
            <Text style={styles.name}>{item.name}</Text>
            <Ionicons name={expandedId === item.id ? 'chevron-up' : 'chevron-down'} size={18} color={colors.textLight} />
          </View>
          <Text style={styles.address}>{item.address}, {item.city}, {item.state} {item.zip}</Text>
          <View style={styles.ownerRow}>
            <Ionicons name="business-outline" size={14} color={colors.textLight} />
            <Text style={styles.ownerText} numberOfLines={1}>{item.businessName || '—'} ({item.ownerEmail || '—'})</Text>
            {!item.stripeOnboarded && (
              <View style={styles.stripeBadge}>
                <Text style={styles.stripeBadgeText}>No Stripe</Text>
              </View>
            )}
          </View>
          <View style={styles.metaRow}>
            <Text style={styles.metaText}>{item.bayCount} bay{item.bayCount !== 1 ? 's' : ''}</Text>
            <View style={{ flexDirection: 'row', gap: spacing.sm, alignItems: 'center' }}>
              <TouchableOpacity onPress={() => openEditLocation(item)} hitSlop={8}>
                <Ionicons name="create-outline" size={18} color={colors.primary} />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setConfirmDeleteLocation(confirmDeleteLocation === item.id ? null : item.id)} hitSlop={8}>
                <Ionicons name="trash-outline" size={18} color={colors.danger || '#dc2626'} />
              </TouchableOpacity>
            </View>
          </View>
          <InlineConfirm
            message={`Delete "${item.name}"? This will remove the location and all its bays. This cannot be undone.`}
            visible={confirmDeleteLocation === item.id}
            onConfirm={() => handleDeleteLocation(item.id)}
            onCancel={() => setConfirmDeleteLocation(null)}
            confirmLabel="Delete"
            loading={deletingLocation}
            variant="danger"
          />
        </View>
      </TouchableOpacity>

      {expandedId === item.id && (
        <View style={styles.baysContainer}>
          {baysLoading ? (
            <ActivityIndicator size="small" color={colors.primary} style={{ padding: spacing.md }} />
          ) : (
            <>
              {locationBays.length === 0 && (
                <Text style={styles.noBays}>No bays</Text>
              )}
              {locationBays.map(bay => (
                <View key={bay.id}>
                  <View style={styles.bayRow}>
                    <View style={[styles.bayDot, { backgroundColor: bay.isActive ? '#059669' : '#dc2626' }]} />
                    <View style={{ flex: 1 }}>
                      <Text style={styles.bayName}>{bay.name}</Text>
                      <Text style={styles.bayMeta} numberOfLines={2}>
                        {bay.type}{bay.pricePerMinute ? ` · $${bay.pricePerMinute.toFixed(2)}/min` : ''}
                        {bay.controllerId ? ` · ${bay.controllerId}` : ' · No controller'}
                      </Text>
                    </View>
                    <View style={{ flexDirection: 'row', gap: spacing.sm, alignItems: 'center' }}>
                      <TouchableOpacity onPress={() => setShowQrBay(bay)} hitSlop={8}>
                        <Ionicons name="qr-code-outline" size={16} color={colors.primary} />
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => openEditBay(bay)} hitSlop={8}>
                        <Ionicons name="create-outline" size={16} color={colors.primary} />
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => router.push(`/admin/owner/${item.ownerId}/location/bay-equipment?bayId=${bay.id}&bayType=${bay.type}&bayName=${encodeURIComponent(bay.name)}` as never)} hitSlop={8}>
                        <Ionicons name="hardware-chip-outline" size={16} color={colors.primary} />
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => setConfirmDeleteBay(confirmDeleteBay === bay.id ? null : bay.id)} hitSlop={8}>
                        <Ionicons name="trash-outline" size={16} color={colors.danger || '#dc2626'} />
                      </TouchableOpacity>
                    </View>
                  </View>
                  {confirmDeleteBay === bay.id && (
                    <View style={{ paddingHorizontal: spacing.md }}>
                      <InlineConfirm
                        message={`Delete bay "${bay.name}"? This cannot be undone.`}
                        visible={true}
                        onConfirm={() => handleDeleteBay(bay.id)}
                        onCancel={() => setConfirmDeleteBay(null)}
                        confirmLabel="Delete"
                        loading={deletingBay}
                        variant="danger"
                      />
                    </View>
                  )}
                </View>
              ))}
              {locationMachines.length > 0 && (
                <>
                  <Text style={[styles.noBays, { textAlign: 'left', fontWeight: '600' as any }]}>Vending</Text>
                  {locationMachines.map(m => (
                    <View key={m.id} style={styles.bayRow}>
                      <View style={[styles.bayDot, { backgroundColor: m.isActive === false ? '#dc2626' : '#059669' }]} />
                      <View style={{ flex: 1 }}>
                        <Text style={styles.bayName}>{m.name}</Text>
                        <Text style={styles.bayMeta} numberOfLines={1}>{m.machineType ?? 'vending'}{m.controllerId ? ` · ${m.controllerId}` : ' · No controller'}{m.equipment && m.equipment.controllers > 0 && m.equipment.configured === false ? ' · No relay assigned' : ''}</Text>
                      </View>
                      <TouchableOpacity onPress={() => router.push(`/admin/owner/${item.ownerId}/location/vending-equipment?machineId=${m.id}&machineName=${encodeURIComponent(m.name)}` as never)} hitSlop={8}>
                        <Ionicons name="hardware-chip-outline" size={16} color={colors.primary} />
                      </TouchableOpacity>
                    </View>
                  ))}
                </>
              )}
              <View style={styles.bayActionsRow}>
                <TouchableOpacity style={styles.createBayBtn} onPress={() => openCreateBay(item.id)}>
                  <Ionicons name="add-circle-outline" size={16} color={colors.primary} />
                  <Text style={styles.createBayText}>Create Bay</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.createBayBtn} onPress={() => openCreateBay(item.id, true)}>
                  <Ionicons name="link-outline" size={16} color={colors.warning || '#d97706'} />
                  <Text style={[styles.createBayText, { color: colors.warning || '#d97706' }]}>Recreate from URL</Text>
                </TouchableOpacity>
                {locationBays.length > 0 && (
                  <TouchableOpacity style={styles.createBayBtn} onPress={() => setQrPdfLocation(item)}>
                    <Ionicons name="print-outline" size={16} color={colors.primary} />
                    <Text style={styles.createBayText}>QR PDF</Text>
                  </TouchableOpacity>
                )}
              </View>
            </>
          )}
        </View>
      )}
    </View>
  );

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
      <AdminToolbar
        search={search} onSearchChange={setSearch} onSearch={handleSearch}
        total={total} page={page} limit={20} onPageChange={handlePage}
        csvEndpoint={csvEndpoint} csvFilename="locations.csv" label="location"
      />
      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : (
        <FlatList ListFooterComponent={<PageFooter />}
          data={locations}
          keyExtractor={item => item.id}
          renderItem={renderItem}
          ListEmptyComponent={<Text style={styles.empty}>No locations found</Text>}
        />
      )}

      {/* Edit Location Modal */}
      <Modal visible={editModal === 'location'} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
            <Text style={styles.modalTitle}>Edit Location</Text>
            <ScrollView style={{ maxHeight: isMobile ? height * 0.6 : 500 }} nestedScrollEnabled>
              {editData && <>
                <EditField label="Name" value={editData.name} onChangeText={(v: string) => setEditData({ ...editData, name: v })} styles={styles} />
                <EditField label="Company Name" value={editData.companyName} onChangeText={(v: string) => setEditData({ ...editData, companyName: v })} styles={styles} />
                <EditField label="Address" value={editData.address} onChangeText={(v: string) => setEditData({ ...editData, address: v })} styles={styles} />
                <View style={{ flexDirection: 'row', gap: spacing.sm }}>
                  <View style={{ flex: 2 }}><EditField label="City" value={editData.city} onChangeText={(v: string) => setEditData({ ...editData, city: v })} styles={styles} /></View>
                  <View style={{ flex: 1 }}><EditField label="State" value={editData.state} onChangeText={(v: string) => setEditData({ ...editData, state: v })} styles={styles} /></View>
                  <View style={{ flex: 1 }}><EditField label="ZIP" value={editData.zip} onChangeText={(v: string) => setEditData({ ...editData, zip: v })} styles={styles} /></View>
                </View>
                <EditField label="Phone" value={editData.phone} onChangeText={(v: string) => setEditData({ ...editData, phone: v })} styles={styles} />
                <EditField label="Description" value={editData.description} onChangeText={(v: string) => setEditData({ ...editData, description: v })} multiline maxLength={500} styles={styles} />
                <Text style={[styles.fieldLabel, { marginTop: spacing.sm, marginBottom: 4 }]}>Business Hours</Text>
                {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(day => (
                  <View key={day} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4, gap: 8 }}>
                    <Text style={{ width: rs(36), fontSize: fontSize.xs, color: colors.textSecondary, textTransform: 'capitalize' }}>{day}</Text>
                    <TextInput
                      style={[styles.fieldInput, { flex: 1, marginBottom: 0 }]}
                      placeholder="e.g. 6:00 AM - 10:00 PM"
                      placeholderTextColor={colors.textLight}
                      value={editData.hours?.[day] || ''}
                      onChangeText={(v: string) => setEditData({ ...editData, hours: { ...editData.hours, [day]: v } })}
                      maxLength={32}
                    />
                  </View>
                ))}
                <TouchableOpacity style={styles.toggleRow} onPress={() => setEditData({ ...editData, isActive: !editData.isActive })}>
                  <Ionicons name={editData.isActive ? 'checkmark-circle' : 'close-circle'} size={24} color={editData.isActive ? '#059669' : '#dc2626'} />
                  <Text style={styles.toggleText}>{editData.isActive ? 'Active' : 'Inactive'}</Text>
                </TouchableOpacity>
              </>}
            </ScrollView>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.modalCancel} onPress={() => setEditModal(null)}><Text style={styles.modalCancelText}>Cancel</Text></TouchableOpacity>
              <TouchableOpacity style={styles.modalSave} onPress={saveLocation} disabled={saving}><Text style={styles.modalSaveText}>{saving ? 'Saving...' : 'Save'}</Text></TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Edit Bay Modal */}
      <Modal visible={editModal === 'bay'} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
            <Text style={styles.modalTitle}>Edit Bay</Text>
            <ScrollView style={{ maxHeight: isMobile ? height * 0.6 : 500 }} nestedScrollEnabled>
              {editData && <>
                <EditField label="Name" value={editData.name} onChangeText={(v: string) => setEditData({ ...editData, name: v })} styles={styles} />
                <Text style={[styles.fieldLabel, { marginBottom: 4 }]}>Type</Text>
                <View style={{ flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm, flexWrap: 'wrap' }}>
                  {['self-serve', 'automatic', 'vacuum'].map(t => (
                    <TouchableOpacity key={t} onPress={() => setEditData({ ...editData, type: t })}
                      style={[styles.typeChip, editData.type === t && styles.typeChipActive]}>
                      <Text style={[styles.typeChipText, editData.type === t && { color: colors.white }]}>
                        {t === 'self-serve' ? 'Self-Serve' : t === 'vacuum' ? 'Vacuum' : 'Automatic'}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
                <EditField label="Controller ID" value={editData.controllerId} onChangeText={(v: string) => setEditData({ ...editData, controllerId: v })} styles={styles} />
                {editData.type === 'self-serve' || editData.type === 'vacuum' ? (
                  <>
                    <EditField label="This unit's rate ($/min) — required for self-serve and vacuums (packages mode included); each bay/vacuum is priced independently" value={editData.pricePerMinute} onChangeText={(v: string) => setEditData({ ...editData, pricePerMinute: v })} keyboardType="decimal-pad" styles={styles} />
                    <View style={{ flexDirection: 'row', gap: spacing.sm }}>
                      <View style={{ flex: 1 }}><EditField label="Min Charge (cents)" value={editData.minimumCharge} onChangeText={(v: string) => setEditData({ ...editData, minimumCharge: v })} keyboardType="number-pad" styles={styles} /></View>
                      <View style={{ flex: 1 }}><EditField label="Max Charge (cents) — metered only (unused when packages exist)" value={editData.maximumCharge} onChangeText={(v: string) => setEditData({ ...editData, maximumCharge: v })} keyboardType="number-pad" styles={styles} /></View>
                    </View>
                    <VacTierRows
                      rows={editData.vacTiers || []}
                      onChange={(rows) => setEditData({ ...editData, vacTiers: rows })}
                      rate={editData.pricePerMinute}
                      styles={styles}
                      colors={colors}
                    />
                    <EditField label="Legacy presets (comma-separated cents) — superseded by packages" value={editData.presets} onChangeText={(v: string) => setEditData({ ...editData, presets: v })} styles={styles} />
                    <TouchableOpacity style={styles.toggleRow} onPress={() => setEditData({ ...editData, allowCustomAmount: !editData.allowCustomAmount })}>
                      <Ionicons name={editData.allowCustomAmount ? 'checkmark-circle' : 'close-circle'} size={24} color={editData.allowCustomAmount ? '#059669' : '#6b7280'} />
                      <Text style={styles.toggleText}>Allow Custom Amounts (converted at the rate)</Text>
                    </TouchableOpacity>
                  </>
                ) : (
                  <EditField label='Tiers JSON — [{"name":"Basic","price":500,"enabled":true}]; set enabled:false to hide a tier from customers' value={editData.tiers} onChangeText={(v: string) => setEditData({ ...editData, tiers: v })} multiline styles={styles} />
                )}
                <TouchableOpacity style={styles.toggleRow} onPress={() => setEditData({ ...editData, isActive: !editData.isActive })}>
                  <Ionicons name={editData.isActive ? 'checkmark-circle' : 'close-circle'} size={24} color={editData.isActive ? '#059669' : '#dc2626'} />
                  <Text style={styles.toggleText}>{editData.isActive ? 'Active' : 'Inactive'}</Text>
                </TouchableOpacity>
              </>}
            </ScrollView>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.modalCancel} onPress={() => setEditModal(null)}><Text style={styles.modalCancelText}>Cancel</Text></TouchableOpacity>
              <TouchableOpacity style={styles.modalSave} onPress={saveBay} disabled={saving}><Text style={styles.modalSaveText}>{saving ? 'Saving...' : 'Save'}</Text></TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Create Bay Modal */}
      <Modal visible={createBayModal} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
            <Text style={styles.modalTitle}>{createBayData?.recreateMode ? 'Recreate Deleted Bay' : 'Create Bay'}</Text>
            <ScrollView style={{ maxHeight: isMobile ? height * 0.6 : 500 }} nestedScrollEnabled>
              {createBayData && <>
                {createBayData.recreateMode && (
                  <View style={{ marginBottom: spacing.sm, padding: spacing.sm, backgroundColor: (colors.warning || '#d97706') + '10', borderRadius: borderRadius.md, borderWidth: 1, borderColor: (colors.warning || '#d97706') + '30' }}>
                    <Text style={{ fontSize: fontSize.xs, color: colors.warning || '#d97706' }}>Paste the old wash URL or bay UUID below. The recreated bay will use the same ID so existing QR codes keep working.</Text>
                  </View>
                )}
                <EditField label={createBayData.recreateMode ? "Old Wash URL or Bay UUID" : "Old URL / Bay UUID (optional — to recreate a deleted bay)"} value={createBayData.oldUrl} onChangeText={(v: string) => setCreateBayData({ ...createBayData, oldUrl: v })} styles={styles} />
                <EditField label="Name" value={createBayData.name} onChangeText={(v: string) => setCreateBayData({ ...createBayData, name: v })} styles={styles} />
                <Text style={[styles.fieldLabel, { marginBottom: 4 }]}>Type</Text>
                <View style={{ flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm, flexWrap: 'wrap' }}>
                  {['self-serve', 'automatic', 'vacuum'].map(t => (
                    <TouchableOpacity key={t} onPress={() => setCreateBayData({ ...createBayData, type: t })}
                      style={[styles.typeChip, createBayData.type === t && styles.typeChipActive]}>
                      <Text style={[styles.typeChipText, createBayData.type === t && { color: colors.white }]}>
                        {t === 'self-serve' ? 'Self-Serve' : t === 'vacuum' ? 'Vacuum' : 'Automatic'}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
                <EditField label="Controller ID" value={createBayData.controllerId} onChangeText={(v: string) => setCreateBayData({ ...createBayData, controllerId: v })} styles={styles} />
                {createBayData.type === 'self-serve' || createBayData.type === 'vacuum' ? (
                  <>
                    <EditField label="This unit's rate ($/min) — required for self-serve and vacuums (packages mode included); each bay/vacuum is priced independently" value={createBayData.pricePerMinute} onChangeText={(v: string) => setCreateBayData({ ...createBayData, pricePerMinute: v })} keyboardType="decimal-pad" styles={styles} />
                    <View style={{ flexDirection: 'row', gap: spacing.sm }}>
                      <View style={{ flex: 1 }}><EditField label="Min Charge (cents)" value={createBayData.minimumCharge} onChangeText={(v: string) => setCreateBayData({ ...createBayData, minimumCharge: v })} keyboardType="number-pad" styles={styles} /></View>
                      <View style={{ flex: 1 }}><EditField label="Max Charge (cents) — metered only (unused when packages exist)" value={createBayData.maximumCharge} onChangeText={(v: string) => setCreateBayData({ ...createBayData, maximumCharge: v })} keyboardType="number-pad" styles={styles} /></View>
                    </View>
                    <VacTierRows
                      rows={createBayData.vacTiers || []}
                      onChange={(rows) => setCreateBayData({ ...createBayData, vacTiers: rows })}
                      rate={createBayData.pricePerMinute}
                      styles={styles}
                      colors={colors}
                    />
                    <EditField label="Legacy presets (comma-separated cents) — superseded by packages" value={createBayData.presets} onChangeText={(v: string) => setCreateBayData({ ...createBayData, presets: v })} styles={styles} />
                    <TouchableOpacity style={styles.toggleRow} onPress={() => setCreateBayData({ ...createBayData, allowCustomAmount: !createBayData.allowCustomAmount })}>
                      <Ionicons name={createBayData.allowCustomAmount ? 'checkmark-circle' : 'close-circle'} size={24} color={createBayData.allowCustomAmount ? '#059669' : '#6b7280'} />
                      <Text style={styles.toggleText}>Allow Custom Amounts (converted at the rate)</Text>
                    </TouchableOpacity>
                  </>
                ) : (
                  <EditField label='Tiers JSON — [{"name":"Basic","price":500,"enabled":true}]; set enabled:false to hide a tier from customers' value={createBayData.tiers} onChangeText={(v: string) => setCreateBayData({ ...createBayData, tiers: v })} multiline styles={styles} />
                )}
                <TouchableOpacity style={styles.toggleRow} onPress={() => setCreateBayData({ ...createBayData, isActive: !createBayData.isActive })}>
                  <Ionicons name={createBayData.isActive ? 'checkmark-circle' : 'close-circle'} size={24} color={createBayData.isActive ? '#059669' : '#dc2626'} />
                  <Text style={styles.toggleText}>{createBayData.isActive ? 'Active' : 'Inactive'}</Text>
                </TouchableOpacity>
              </>}
            </ScrollView>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.modalCancel} onPress={() => setCreateBayModal(false)}><Text style={styles.modalCancelText}>Cancel</Text></TouchableOpacity>
              <TouchableOpacity style={styles.modalSave} onPress={createBay} disabled={creating}><Text style={styles.modalSaveText}>{creating ? 'Creating...' : 'Create'}</Text></TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Bay QR Code Modal */}
      {showQrBay && (
        <Modal visible transparent animationType="fade" onRequestClose={() => setShowQrBay(null)}>
          <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setShowQrBay(null)}>
            <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
              <Text style={styles.modalTitle}>Bay QR Code — {showQrBay.name}</Text>
              {showQrBay.qrCodeUrl ? (
                <View style={styles.qrContainer}>
                  <QrSvgView uri={showQrBay.qrCodeUrl} width={Math.min(width * 0.7, 280)} accessibilityLabel={`QR code for ${showQrBay.name}`} />
                </View>
              ) : (
                <Text style={styles.qrDeepLink}>No QR code stored for this bay. Save the bay with a new name to regenerate it.</Text>
              )}
              <Text style={styles.qrDeepLink} selectable numberOfLines={2}>{showQrBay.deepLink}</Text>
              {showQrBay.qrCodeUrl && (
                <TouchableOpacity
                  style={styles.downloadBtn}
                  onPress={async () => {
                    try {
                      const safeName = showQrBay.name.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-');
                      await downloadFile(showQrBay.qrCodeUrl!, `rinsepay-qr-${safeName}.svg`);
                    } catch (err) { console.error('QR download failed:', err); }
                  }}
                >
                  <Ionicons name="download-outline" size={18} color="#fff" />
                  <Text style={styles.downloadBtnText}>Download SVG</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity style={styles.modalCancel} onPress={() => setShowQrBay(null)}>
                <Text style={[styles.modalCancelText, { textAlign: 'center' }]}>Close</Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </Modal>
      )}

      {/* Aggregate QR PDF Modal */}
      {qrPdfLocation && (
        <Modal visible transparent animationType="fade" onRequestClose={() => setQrPdfLocation(null)}>
          <View style={styles.modalOverlay}>
            <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
              <Text style={styles.modalTitle}>Print Bay QR Codes</Text>
              <Text style={styles.qrPdfHint}>
                One labeled QR per bay at {qrPdfLocation.name}, tiled into a printable PDF.
                Each label is the full branded sign (company name, QR, bay name). Size sets the label width in px (50–2000); height keeps its ratio.
              </Text>
              <EditField label="Label width (px)" value={qrPdfSize} onChangeText={setQrPdfSize} keyboardType="number-pad" styles={styles} />
              <View style={styles.modalActions}>
                <TouchableOpacity style={styles.modalCancel} onPress={() => setQrPdfLocation(null)}>
                  <Text style={styles.modalCancelText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.modalSave} onPress={downloadQrPdf} disabled={qrPdfDownloading}>
                  <Text style={styles.modalSaveText}>{qrPdfDownloading ? 'Generating...' : 'Download PDF'}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      )}
      </ContentContainer>
    </View>
  );
}

interface VacTierRow { name: string; price: string; duration: string }

/** Structured timed-package editor (same model as the owner bay screens):
 *  up to 6 rows of Name | Price ($) | Minutes. Typing a price auto-fills the
 *  minutes from the master rate (overridable). No rows = packages family off.
 *  Module-level so its identity is stable across renders. */
function VacTierRows({ rows, onChange, rate, styles, colors }: {
  rows: VacTierRow[];
  onChange: (rows: VacTierRow[]) => void;
  /** Master rate in $/min — drives the price → minutes auto-fill. */
  rate?: string;
  styles: ReturnType<typeof createStyles>;
  colors: ColorTheme;
}) {
  // Bulk-discount override: unlock minutes from the rate math. Pre-checked
  // when any existing row is priced off the rate curve.
  const [overrideMinutes, setOverrideMinutes] = React.useState(() => {
    const rateNum = parseFloat(rate || '');
    return rateNum > 0 && rows.some(r => {
      const price = parseFloat(r.price);
      const mins = parseFloat(r.duration);
      return price > 0 && mins > 0 && Math.abs(price - mins * rateNum) > 0.011;
    });
  });
  const update = (i: number, field: keyof VacTierRow, v: string) => {
    const u = [...rows];
    u[i] = { ...u[i], [field]: v };
    // Bidirectional via the master rate; override unlocks minutes (price
    // still fills an EMPTY minutes as a hint).
    const rateNum = parseFloat(rate || '');
    if (field === 'price' && (!overrideMinutes || !u[i].duration)) {
      const price = parseFloat(v);
      if (price > 0 && rateNum > 0) u[i] = { ...u[i], duration: String(Math.max(1, Math.round(price / rateNum))) };
    } else if (field === 'duration' && !overrideMinutes) {
      const mins = parseFloat(v);
      if (mins > 0 && rateNum > 0) u[i] = { ...u[i], price: (mins * rateNum).toFixed(2) };
    }
    onChange(u);
  };
  return (
    <View style={{ marginBottom: spacing.sm }}>
      <Text style={styles.fieldLabel}>Timed Packages — rows present = count down (metered off)</Text>
      <TouchableOpacity
        style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.xs }}
        onPress={() => setOverrideMinutes(!overrideMinutes)}
      >
        <Ionicons name={overrideMinutes ? 'checkbox' : 'square-outline'} size={20} color={overrideMinutes ? colors.primary : colors.textLight} />
        <Text style={styles.fieldLabel}>Override minutes (bulk discounts)</Text>
      </TouchableOpacity>
      {rows.length > 0 && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: 2 }}>
          <Text style={[styles.fieldLabel, { flex: 2, marginBottom: 0 }]}>Name</Text>
          <Text style={[styles.fieldLabel, { flex: 1, marginBottom: 0 }]}>Price</Text>
          <Text style={[styles.fieldLabel, { flex: 1, marginBottom: 0 }]}>Minutes</Text>
          <View style={{ width: 20 }} />
        </View>
      )}
      {rows.map((r, i) => (
        <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: 4 }}>
          <TextInput style={[styles.fieldInput, { flex: 2 }]} placeholder="Name" placeholderTextColor={colors.textLight} value={r.name} onChangeText={(v) => update(i, 'name', v)} maxLength={32} />
          <TextInput style={[styles.fieldInput, { flex: 1 }]} placeholder="$" placeholderTextColor={colors.textLight} value={r.price} onChangeText={(v) => update(i, 'price', v.replace(/[^0-9.]/g, ''))} keyboardType="decimal-pad" />
          <TextInput style={[styles.fieldInput, { flex: 1 }]} placeholder="Min" placeholderTextColor={colors.textLight} value={r.duration} onChangeText={(v) => update(i, 'duration', v.replace(/[^0-9]/g, ''))} keyboardType="number-pad" />
          <TouchableOpacity onPress={() => onChange(rows.filter((_, idx) => idx !== i))} hitSlop={8} style={{ width: 20 }}>
            <Ionicons name="close-circle" size={20} color={colors.danger} />
          </TouchableOpacity>
        </View>
      ))}
      {rows.length < 6 && (
        <TouchableOpacity
          onPress={() => onChange([...rows, { name: '', price: '', duration: '' }])}
          style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: spacing.xs }}
        >
          <Ionicons name="add-circle-outline" size={18} color={colors.primary} />
          <Text style={{ fontSize: fontSize.xs, color: colors.primary, fontWeight: fontWeight.semibold }}>Add Package</Text>
        </TouchableOpacity>
      )}
      <Text style={{ fontSize: fontSize.xs, color: colors.textLight, marginTop: 2 }}>
        Price auto-fills minutes from the rate; edit minutes to override.
      </Text>
    </View>
  );
}

function EditField({ label, value, onChangeText, multiline, keyboardType, maxLength, styles }: { label: string; value: string; onChangeText: (v: string) => void; multiline?: boolean; keyboardType?: string; maxLength?: number; styles: ReturnType<typeof createStyles> }) {
  return (
    <View style={styles.fieldWrap}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <TextInput style={[styles.fieldInput, multiline && { height: 80, textAlignVertical: 'top' }]} value={value} onChangeText={onChangeText} multiline={multiline} maxLength={maxLength ?? (multiline ? 500 : 100)} keyboardType={keyboardType as import('react-native').KeyboardTypeOptions} />
    </View>
  );
}

const createStyles = (c: ColorTheme, isMobile: boolean, height: number) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  row: { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: 10 },
  statusIcon: { width: rs(32), height: rs(32), borderRadius: rs(16), justifyContent: 'center', alignItems: 'center', marginTop: 2 },
  rowBody: { flex: 1 },
  rowHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  name: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  address: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  ownerRow: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 4 },
  ownerText: { fontSize: fontSize.xs, color: c.textLight, flex: 1 },
  stripeBadge: { backgroundColor: '#fef3c7', paddingHorizontal: 6, paddingVertical: 1, borderRadius: 4, marginLeft: 4 },
  stripeBadgeText: { fontSize: fs(10), color: '#92400e', fontWeight: fontWeight.semibold },
  metaRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 },
  metaText: { fontSize: fontSize.xs, color: c.textLight },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.xl, fontSize: fontSize.md },

  // Bays
  baysContainer: { backgroundColor: c.surfaceSecondary || '#f8fafc', marginLeft: isMobile ? spacing.md : 42, borderRadius: borderRadius.md, marginBottom: spacing.sm },
  noBays: { padding: spacing.md, fontSize: fontSize.xs, color: c.textLight, textAlign: 'center' },
  bayRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: spacing.md, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: 8 },
  bayDot: { width: 8, height: 8, borderRadius: 4 },
  bayName: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  bayMeta: { fontSize: fontSize.xs, color: c.textLight },
  bayActionsRow: { flexDirection: 'row', justifyContent: 'center', gap: spacing.lg, borderTopWidth: 1, borderTopColor: c.borderLight },
  createBayBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4, paddingVertical: 8 },
  createBayText: { fontSize: fontSize.xs, color: c.primary, fontWeight: fontWeight.semibold },

  // Modal
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: isMobile ? 'flex-end' : 'center', alignItems: 'center', padding: isMobile ? 0 : spacing.lg },
  modalContent: { backgroundColor: c.surface, borderRadius: isMobile ? 0 : borderRadius.lg, borderTopLeftRadius: borderRadius.lg, borderTopRightRadius: borderRadius.lg, padding: spacing.lg, width: '100%', maxWidth: isMobile ? undefined : 500 },
  modalTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.md },
  modalCancel: { paddingVertical: spacing.sm, paddingHorizontal: spacing.lg, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.border },
  modalCancelText: { color: c.textSecondary, fontWeight: fontWeight.medium },
  modalSave: { paddingVertical: spacing.sm, paddingHorizontal: spacing.lg, borderRadius: borderRadius.md, backgroundColor: c.primary },
  modalSaveText: { color: c.white, fontWeight: fontWeight.semibold },
  toggleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm },
  toggleText: { fontSize: fontSize.md, color: c.text },

  // Bay QR modal
  qrContainer: { alignItems: 'center', marginVertical: spacing.md },
  qrDeepLink: { fontSize: fontSize.xs, color: c.textSecondary, textAlign: 'center', marginBottom: spacing.md },
  downloadBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, backgroundColor: c.primary, borderRadius: borderRadius.md, paddingVertical: spacing.sm, marginBottom: spacing.sm },
  downloadBtnText: { color: c.white, fontWeight: fontWeight.semibold, fontSize: fontSize.sm },
  qrPdfHint: { fontSize: fontSize.xs, color: c.textSecondary, marginBottom: spacing.md },

  // Edit fields
  fieldWrap: { marginBottom: spacing.sm },
  fieldLabel: { fontSize: fontSize.xs, color: c.textSecondary, marginBottom: 2 },
  fieldInput: { borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, fontSize: fontSize.sm, color: c.text, backgroundColor: c.surface },
  typeChip: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: borderRadius.md, backgroundColor: c.surfaceSecondary, borderWidth: 1, borderColor: c.border },
  typeChipActive: { backgroundColor: c.primary, borderColor: c.primary },
  typeChipText: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.textSecondary },
});

const useStyles = (c: ColorTheme, isMobile: boolean, height: number) => React.useMemo(() => createStyles(c, isMobile, height), [c, isMobile, height]);
